import { useRef, useState } from "react";
import useOnClickOutside from "../../hooks/useOnClickOutside";
import SvgMoreVert from "../icons/MoreVert";
import Icon from "./Icon";
import styles from "./ListItemMenu.module.css";

type XOR<T, U> = (T & { [K in keyof U]?: never }) | (U & { [K in keyof T]?: never });

export type ListItemMenuAction<T> = {
  onClick: (item: T) => void;
  isDisabled?: (item: T) => boolean;
} & XOR<{ title: string }, { getTitle: (item: T) => string }>;

interface ListItemMenuProps<T> {
  actions: ListItemMenuAction<T>[];
  item: T;
}

const ListItemMenu = <T,>({ actions, item }: ListItemMenuProps<T>) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ left: number; top: number } | null>(null);

  const menuRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useOnClickOutside(menuRef, () => setModalOpen(false));

  const handleIconClick = (e: React.MouseEvent<Element, MouseEvent>) => {
    const { pageX, pageY, clientX, clientY } = e;

    setModalOpen(true);

    requestAnimationFrame(() => {
      if (menuRef.current) {
        const menuWidth = menuRef.current.offsetWidth;
        const menuHeight = menuRef.current.offsetHeight;

        // Calculate the position based on click coordinates
        let newLeft = pageX - menuWidth; // Right-upper corner at click point
        let newTop = pageY;

        // Ensure the menu doesn’t overflow the screen
        if (pageX < menuWidth) newLeft = clientX; // Prevent left overflow
        if (window.innerHeight - clientY < menuHeight) newTop = pageY - menuHeight; // Prevent bottom overflow

        setMenuPosition({ left: newLeft, top: newTop });
      }
    });
  };

  return (
    <div ref={containerRef}>
      <Icon glyph={SvgMoreVert} onClick={handleIconClick} className={styles.icon} />
      {isModalOpen && (
        <div
          className={styles.menu}
          ref={menuRef}
          style={{
            left: `${menuPosition?.left}px`,
            top: `${menuPosition?.top}px`,
            visibility: menuPosition ? "visible" : "hidden",
          }}
        >
          <ul className={styles.list}>
            {actions.map((action, index) => {
              const isDisabled = action.isDisabled?.(item);
              return (
                <li
                  key={index}
                  className={isDisabled ? styles.disabled : undefined}
                  onClick={() => {
                    if (!isDisabled) {
                      setModalOpen(false);
                      action.onClick(item);
                    }
                  }}
                >
                  {action.title ?? action.getTitle?.(item)}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

export default ListItemMenu;
